// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { Location } from 'history';

import { ShopBadges } from '@reverbdotcom/commons/src/components/shop_badges';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { SHOP_PATH } from '@reverbdotcom/commons/src/url_helpers';
import URLHelpers, { adminEditUserPath } from './shared/url_helpers';
import ShopPoliciesModal from './item2_shop_policies_modal';
import { getYear } from '../lib/date_utils';
import ShopSvg from '@reverbdotcom/cadence/images/brand/my-shop-orange.svg';

import {
  BumpKey,
  Item2ShopModuleData,
} from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { RCAccordion } from '@reverbdotcom/cadence/components';
import { Item2ShopReviews, Item2ShopReviewsFragment } from './item2_shop_reviews';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface IProps {
  listing: Item2ShopModuleData.Fragment & BumpKey.Fragment;
  location?: Location;
}

export const Item2ShopModuleFragment = gql`
  fragment Item2ShopModuleData on Listing {
    _id
    id
    seller {
      _id
      id
      uuid
      avatar {
        source
      }
    }
    shop {
      _id
      name
      slug
      preferredSeller
      quickShipper
      quickResponder
      address {
        _id
        displayLocation
        countryCode
      }
      createdAt
      orders {
        total
      }
    }
    ...Item2ShopReviewsData
  }
  ${Item2ShopReviewsFragment}
`;

export function componentClickEvent(displayStyle, path) {
  return {
    eventName: MParticleEventName.ClickedComponent,
    componentName: 'Item2ShopModule',
    displayStyle,
    entityType: 'url',
    entityId: path,
  };
}

export function Item2ShopModule({ listing, location }: IProps) {
  const { shop, seller } = listing;
  const user = useUser();

  if (!shop) return null;

  const { preferredSeller, quickResponder, quickShipper } = shop;
  const shopURL = SHOP_PATH.expand({ slug: shop.slug });
  const messageURL = URLHelpers.newDashboardMessagesPath(
    listing.id,
    seller?.uuid,
    listing.bumpKey?.key,
  );

  const avatarUrl = seller?.avatar?.source;
  const imageClasses = classNames(
    'item2-shop-module__image',
    { 'item2-shop-module__image--placeholder': !avatarUrl },
  );

  return (
    <RCAccordion
      defaultOpen
      locked
      size="large"
      heading={I18n.t('discovery.item2ShopModule.aboutTheSeller')}
      id="shop-module"
    >
      <div className="item2-shop-module">
        <div className="item2-shop-module__header">
          <CoreLink
            to={shopURL}
            className="item2-shop-module__image-link"
            clickEvent={componentClickEvent('imageLink', shopURL)}
          >
            <div className={imageClasses}>
              <img
                src={avatarUrl || ShopSvg}
                alt=""
              />
            </div>
          </CoreLink>
          <div className="item2-shop-module__content">
            <h4>
              <CoreLink
                to={shopURL}
                className="item2-shop-module__title"
                clickEvent={componentClickEvent('titleLink', shopURL)}
              >
                {shop.name}
              </CoreLink>
              {user.isAdmin &&
                <CoreLink
                  to={adminEditUserPath.expand({ id: seller.id })}
                  className="ml-1 size-140 color-accent"
                  data-user-edit
                >
                  <span className="fa fa-magic" />
                </CoreLink>
              }
            </h4>
            <div className="item2-shop-module__location">
              {shop.address?.displayLocation}
            </div>
            <div className="d-flex fx-wrap">
              <div className="item2-shop-module__stats">
                {shop.orders?.total > 0 &&
                  <div>
                    <span className="mr-space weight-bold">
                      {I18n.t('discovery.item2Shop.sales')}:
                    </span>
                    {shop.orders?.total.toLocaleString()}
                  </div>
                }
                <div>
                  <span className="mr-space weight-bold">
                    {I18n.t('discovery.item2Shop.joined')}:
                  </span>
                  {getYear(shop.createdAt)}
                </div>
              </div>
              <ShopBadges
                preferredSeller={preferredSeller}
                quickShipper={quickShipper}
                quickResponder={quickResponder}
                hideLabels
              />
            </div>
          </div>
        </div>
        <div className="item2-shop-module__actions">
          <div className="item2-shop-module__action">
            <CoreLink
              to={messageURL}
              className="item2-shop-module__action__button"
              data-registration="required"
              data-send-message
              clickEvent={componentClickEvent('messageSellerLink', messageURL)}
            >
              {I18n.t('discovery.item2Shop.messageSeller')}
            </CoreLink>
          </div>
          <div className="item2-shop-module__action">
            <CoreLink
              to={shopURL}
              className="item2-shop-module__action__button"
              clickEvent={componentClickEvent('viewShopLink', shopURL)}
            >
              {I18n.t('discovery.item2ShopModule.viewShop')}
            </CoreLink>
          </div>
        </div>
        <div className="item2-shop-module__policies">
          <ShopPoliciesModal
            listingId={listing.id}
            clickEvent={{
              eventName: MParticleEventName.ClickedI2ShopPoliciesModal,
              componentName: 'Item2ShopModule',
              listingId: listing.id,
            }}
          />
        </div>
        <div className="item2-shop-module__reviews">
          <Item2ShopReviews
            listing={listing}
            location={location}
            heading={I18n.t('discovery.item2Layout.sellerReviews')}
          />
        </div>
      </div>
    </RCAccordion>
  );
}
